'use client';

import {
  readFromLocalStorage,
  removeFromLocalStorage,
  writeToLocalStorage,
} from '@/lib/local-storage/local-storage';
import { useBulkTabStore } from '@/store';
import type { TrackedBulk } from '@/store/types';
import { datadogRum } from '@datadog/browser-rum';
import {
  getConfig,
  type BaseInstance,
  type IBaseInstanceConfig,
  type ModelName,
} from '@pigello/pigello-matrix';
import { nanoid } from 'nanoid';
import { usePathname } from 'next/navigation';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

declare global {
  interface Window {
    _minimize_bulk: (trackedBulk: TrackedBulk) => void;
  }
}

const BULK_MODE_REQUIREMENTS: Record<
  TrackedBulk['mode'],
  {
    instances: boolean;
  }
> = {
  create: {
    instances: false,
  },
  copy: {
    instances: true,
  },
  edit: {
    instances: true,
  },
  download: {
    instances: false,
  },
};

export type Preview = {
  type: 'instance' | 'bulks' | 'nonsavedinstance';
  display: string;
  chapterSuffix?: string;
  identifier: string;
  breadcrumbs?: Breadcrumb[];
  instanceId?: string;
} & (
  | {
      type: 'instance';
      instanceId: string;
    }
  | {
      type: 'nonsavedinstance';
      zIdentifier: string;
    }
  | {
      type: 'bulks';
    }
);

type Breadcrumb = {
  title: string;
  id: string;
  key: string;
  path: string;
};

type Stored = {
  type: Preview['type'];
  chapterSuffix?: string;
  identifier: string;
} & (
  | {
      type: 'instance';
      detailPath: string;
    }
  | {
      type: 'nonsavedinstance';
      formPath?: string;
    }
  | {
      type: 'bulks';
      bulkId: string;
      modelName: ModelName;
      mode: TrackedBulk['mode'];
    }
);

interface State {
  addInstance: (
    instance: BaseInstance,
    config: IBaseInstanceConfig<BaseInstance>,
    detailPath: string
  ) => string;
  addNonSavedInstance: (
    config: IBaseInstanceConfig<BaseInstance>,
    zIdentifier: string,
    display: string,
    options?: {
      chapterSuffix?: string;
    },
    formPath?: string
  ) => string | undefined;
  removePreview: (identifier: string) => void;
  getStored: (identifier: string) => Stored | null;
  previews: Array<Preview>;
  resetPreviews: () => void;
  openBulk: (
    modelName: ModelName,
    type: TrackedBulk['mode'],
    instances?: string[]
  ) => void;
  addBreadcrumb: (bc: Breadcrumb | Breadcrumb[]) => void;
  breadcrumbs: Breadcrumb[];
  navBreadcrumb: (index: number) => void;
}

const initialState: State = {
  addInstance: () => 'undefined',
  addNonSavedInstance: () => 'undefined',
  getStored: () => ({
    identifier: '',
    type: 'instance',
    queryParams: {},
    detailPath: '',
  }),
  removePreview: () => undefined,
  previews: [],
  resetPreviews: () => undefined,
  openBulk: () => undefined,
  addBreadcrumb: () => undefined,
  breadcrumbs: [],
  navBreadcrumb: () => undefined,
};

export const WindowKeeperContext = createContext(initialState);

const LOCAL_STORAGE_PREVIEWS_KEY = '__pigello__wk__previews';
const LOCAL_STORAGE_STORAGE_KEY = '__pigello__wk__storage';

export default function Provider({ children }: { children: ReactNode }) {
  const [previews, setPreviews] = useState<Array<Preview>>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);

  const storePreview = useCallback((preview: Preview, toStore: Stored) => {
    writeToLocalStorage({
      key: `${LOCAL_STORAGE_STORAGE_KEY}-${preview.identifier}`,
      value: toStore,
    });

    writeToLocalStorage({
      key: `${LOCAL_STORAGE_PREVIEWS_KEY}-${preview.identifier}`,
      value: preview,
    });
  }, []);

  const addNonSavedInstance = useCallback(
    (
      config: IBaseInstanceConfig<BaseInstance>,
      zIdentifier: string,
      display: string,
      options?: {
        chapterSuffix?: string;
      },
      formPath?: string
    ) => {
      const found = previews.find((preview) => {
        if (preview.type !== 'nonsavedinstance') return false;
        return preview.identifier === zIdentifier;
      });

      if (found) return;

      const newPreview: Preview = {
        type: 'nonsavedinstance',
        zIdentifier: zIdentifier,
        identifier: zIdentifier,
        chapterSuffix: options?.chapterSuffix,
        display: `${config.modelName}: ${display}`,
      };
      const toStore: Stored = {
        type: 'nonsavedinstance',
        identifier: zIdentifier,
        chapterSuffix: options?.chapterSuffix,
        formPath,
      };

      storePreview(newPreview, toStore);
      setPreviews((prev) => [...prev, newPreview]);

      return zIdentifier;
    },
    [previews, storePreview]
  );

  const addInstance = useCallback(
    (
      instance: BaseInstance,
      config: IBaseInstanceConfig<BaseInstance>,
      detailPath: string
    ) => {
      const newPreview: Preview = {
        type: 'instance',
        identifier: `wk_${nanoid()}`,
        display: `${config.modelName}: ${config.getDisplayValue(instance)}`,
        instanceId: instance.id as string,
        breadcrumbs,
      };

      const toStore: Stored = {
        type: 'instance',
        identifier: newPreview.identifier,
        detailPath,
      };

      storePreview(newPreview, toStore);

      setPreviews((prev) => [...prev, newPreview]);

      return newPreview.identifier;
    },
    [breadcrumbs, storePreview]
  );

  const getStored = useCallback((identifier: string): Stored | null => {
    const stored = readFromLocalStorage<Stored | undefined>({
      key: `${LOCAL_STORAGE_STORAGE_KEY}-${identifier}`,
      defaultValue: undefined,
    });

    return stored ?? null;
  }, []);

  const addTrackedBulk = useCallback(
    (trackedBulk: TrackedBulk) => {
      const newPreview: Preview = {
        type: 'bulks',
        identifier: trackedBulk.id,
        display: `${trackedBulk.modelName}:${trackedBulk.displayName}`,
      };

      const toStore: Stored = {
        type: 'bulks',
        identifier: newPreview.identifier,
        //saving the same id twice (with identifier) so identifiers aren't connected to bulk window id
        bulkId: trackedBulk.id,
        modelName: trackedBulk.modelName,
        mode: trackedBulk.mode,
      };

      storePreview(newPreview, toStore);

      setPreviews((prev) => [...prev, newPreview]);
    },
    [storePreview]
  );

  useEffect(() => {
    window._minimize_bulk = addTrackedBulk;
  }, [addTrackedBulk]);

  const removePreview = useCallback((identifier: string) => {
    removeFromLocalStorage({ key: `${LOCAL_STORAGE_PREVIEWS_KEY}-${identifier}` });
    removeFromLocalStorage({ key: `${LOCAL_STORAGE_STORAGE_KEY}-${identifier}` });
    setPreviews((prev) => {
      return prev.filter((preview) => preview.identifier !== identifier);
    });
  }, []);

  const resetPreviews = useCallback(() => {
    // Direct localStorage iteration: the storage wrappers don't expose prefix scans,
    // and previews are stored under dynamic per-identifier keys.
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k !== null) keys.push(k);
    }
    for (const key of keys) {
      if (
        key.includes(LOCAL_STORAGE_PREVIEWS_KEY) ||
        key.includes(LOCAL_STORAGE_STORAGE_KEY)
      ) {
        try {
          localStorage.removeItem(key);
        } catch (err) {
          datadogRum.addError(err);
        }
      }
    }
    setPreviews([]);
  }, []);

  useEffect(() => {
    // Direct localStorage iteration: the storage wrappers don't expose prefix scans,
    // and previews are stored under dynamic per-identifier keys.
    const initialPreviews: Preview[] = [];
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k !== null) keys.push(k);
    }
    for (const key of keys) {
      if (key.includes(LOCAL_STORAGE_PREVIEWS_KEY)) {
        try {
          const raw = localStorage.getItem(key);
          const preview = JSON.parse(raw ?? '');
          const isValidPreview =
            preview &&
            typeof preview === 'object' &&
            typeof preview.identifier === 'string' &&
            'display' in preview;
          if (!isValidPreview) {
            localStorage.removeItem(key);
            continue;
          }
          if (preview.type === 'nonsavedinstance') {
            localStorage.removeItem(key);
            localStorage.removeItem(
              `${LOCAL_STORAGE_STORAGE_KEY}-${preview.identifier}`
            );
            continue;
          }

          initialPreviews.push(preview);
        } catch {
          localStorage.removeItem(key);
        }
      }
    }
    setPreviews(initialPreviews.filter((p) => p.type !== 'nonsavedinstance'));
  }, []);

  const openBulk = useCallback(
    async (
      modelName: ModelName,
      type: TrackedBulk['mode'],
      instances?: string[]
    ) => {
      if (type === 'download')
        throw new Error(
          'Mode: download, in un-supported by window keeper provider'
        );

      if (!(type in BULK_MODE_REQUIREMENTS))
        throw new Error(`${type} isn't a bulk mode`);

      const config = await getConfig(modelName);

      const requireInstances = BULK_MODE_REQUIREMENTS[type].instances;

      if (!requireInstances) {
        useBulkTabStore.getState().openNewBulk({
          config,
          //don't know if this is good. it will work. but looks weird.
          mode: type as 'create',
        });
        return;
      }

      if (!instances) {
        throw new Error(
          `Missing instances. Opening: ${modelName} with mode: ${type}`
        );
      }

      useBulkTabStore.getState().openNewBulk({
        config,
        mode: type,
        instances,
      });
    },
    []
  );

  const addBreadcrumb = useCallback((bc: Breadcrumb | Breadcrumb[]) => {
    if (Array.isArray(bc)) {
      setBreadcrumbs(bc);
    } else {
      setBreadcrumbs((prev) => {
        const hasPreviousId =
          prev[prev.length - 1] && prev[prev.length - 1].id === bc.id;

        const secondToLastItem = prev[prev.length - 2];
        const isGoingBackOneStep =
          secondToLastItem && secondToLastItem.id === bc.id;

        if (isGoingBackOneStep) {
          return prev.slice(0, prev.length - 1);
        } else if (hasPreviousId) {
          return [...prev.slice(0, prev.length - 1), bc];
        } else {
          return [...prev, bc];
        }
      });
    }
  }, []);

  const navBreadcrumb = useCallback((index: number) => {
    setBreadcrumbs((prev) => [...prev.slice(0, index)]);
  }, []);

  const resetBreadcrumbs = useCallback(() => {
    setBreadcrumbs([]);
  }, []);

  const pathname = usePathname();

  useEffect(() => {
    if (!pathname.startsWith('/detail') && breadcrumbs.length > 0) {
      resetBreadcrumbs();
    }
  }, [pathname, resetBreadcrumbs, breadcrumbs]);

  const contextValue = useMemo(() => {
    return {
      addInstance,
      addNonSavedInstance,
      removePreview,
      getStored,
      previews,
      resetPreviews,
      openBulk,
      breadcrumbs,
      addBreadcrumb,
      navBreadcrumb,
    };
  }, [
    addInstance,
    addNonSavedInstance,
    openBulk,
    previews,
    addBreadcrumb,
    breadcrumbs,
    navBreadcrumb,
    removePreview,
    getStored,
    resetPreviews,
  ]);

  return (
    <WindowKeeperContext.Provider value={contextValue}>
      {children}
    </WindowKeeperContext.Provider>
  );
}
